import React from 'react'
import { LicenseDetailsDialog } from './LicenseDetailsDialog'
import { licenseCardLabel } from '../utilities/pageConstants'
import { commonLabelName } from '../utilities/commonConstants'

interface LicenseCardProps {
    productCode: string
    /** Title of the license */
    title: string
    /** Term or duration of the license */
    term: string
    /** Image source URL */
    imageSrc: string
    /** Price of the license */
    price: string
    /** Indicates if the license is selected */
    isSelected?: boolean
    /** Current quantity of the license */
    quantity?: number
    /** Function to set the quantity */
    setQuantity?: (newQuantity: number) => void
    /** Callback function for when the selection state changes */
    onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
    /** Optional flag to indicate if this is an evaluation license */
    isEvaluation?: boolean
}

/**
 * LicenseCard Component
 *
 * This component represents a license selection card with options to increase/decrease quantity,
 * view details, and select the license.
 *
 * @component
 * @param {LicenseCardProps} props - The properties passed to the component.
 * @returns {JSX.Element} A card displaying license details with selection and quantity options.
 */
export function LicenseCard({
    productCode,
    title,
    term,
    imageSrc,
    isSelected,
    onSelect,
    price,
    setQuantity,
    quantity = 0,
    isEvaluation = false
}: LicenseCardProps) {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false)

    /** Increases the quantity by 1 */
    const handleIncrement = () => {
        const newQuantity = quantity + 1
        setQuantity?.(newQuantity)
    }
    /** Decreases the quantity by 1, ensuring it does not go below zero */
    const handleDecrement = () => {
        const newQuantity = quantity - 1
        if (newQuantity >= 0) {
            setQuantity?.(newQuantity)
        }
    }
    // Determine the image URL based on the title
    const imageUrl =
        title.startsWith('IDL') || title === 'Tech Support Premium Academic Support Single User 1-yr'
            ? `${process.env.REACT_APP_LOGO_BASE_URL}/IDL_icon.png`
            : title.includes('ENVI')
              ? `${process.env.REACT_APP_LOGO_BASE_URL}/ENVI_Icon.png`
              : `${process.env.REACT_APP_LOGO_BASE_URL}/placeholderImage.png`

    return (
        <>
            <div
                className={`bg-white rounded-lg border border-gray p-6 flex items-start gap-6 ${
                    isSelected && quantity > 0 ? 'shadow-lg border-primary' : ''
                }`}
            >
                {/* Image Section */}
                <div className='w-24 h-24 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0'>
                    <img src={imageUrl} alt={`preview`} className='w-full h-full object-cover' />
                </div>

                {/* Content Container */}
                <div className='flex flex-1 flex-col justify-between'>
                    <div className='flex flex-col items-start'>
                        <h3 className='text-lg font-semibold text-gray-900 text-left'>{title}</h3>
                        <p className='text-secondary text-md font-normal mt-1 text-left'>{term}</p>
                    </div>
                    <div className='flex items-center justify-between gap-2 mt-2'>
                        <div className='flex items-center gap-2'>
                            <button
                                onClick={handleDecrement}
                                className='w-8 h-8 text-2xl flex self-center justify-center bg-[#e9e9ea] rounded-md text-secondary font-normal'
                                disabled={quantity === 0}
                            >
                                {commonLabelName?.minus}
                            </button>
                            <span className='w-8 text-center text-md font-semibold'>{quantity.toString().padStart(2, '0')}</span>
                            <button
                                onClick={handleIncrement}
                                className='w-8 h-8 text-2xl flex self-center justify-center bg-[#e9e9ea] rounded-md text-secondary font-normal'
                            >
                                {commonLabelName?.plus}
                            </button>
                        </div>
                        {/* Price Section */}
                        {price && <div className='text-2xl font-semibold text-primary'>${price}</div>}
                    </div>

                    <div className='flex justify-between items-center mt-6'>
                        <button onClick={() => setIsDialogOpen(true)} className='text-primary text-sm font-semibold'>
                            {licenseCardLabel?.viewDetails}
                        </button>
                        <div className={`flex items-center gap-2 ${quantity === 0 && 'opacity-50 cursor-not-allowed pointer-events-none'}`}>
                            <label className='relative flex items-center cursor-pointer'>
                                <input
                                    type='checkbox'
                                    name={`select-${title}`}
                                    checked={isSelected}
                                    onChange={onSelect}
                                    className='sr-only peer'
                                    disabled={quantity === 0 && !isSelected}
                                />
                                <span
                                    className={`w-4 h-4 flex justify-center items-center rounded border ${
                                        isSelected && quantity > 0 ? 'bg-[#3dbc51] border-[#3dbc51]' : 'bg-white border-gray-300'
                                    }`}
                                >
                                    {isSelected && (
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                            stroke='white'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            className='w-3 h-3'
                                        >
                                            <path d='M5 13l4 4L19 7' />
                                        </svg>
                                    )}
                                </span>
                                <span
                                    className={`text-sm font-semibold ml-2 ${
                                        isSelected && quantity > 0 ? 'text-lightGreen' : 'text-secondary'
                                    }`}
                                >
                                    {isSelected && quantity > 0 ? 'Selected' : 'Select'}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog */}
            {isDialogOpen && (
                <LicenseDetailsDialog
                    isOpen={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    license={{
                        title: title,
                        imageSrc: 'path/to/image',
                        productCode: productCode
                    }}
                    isEvaluation={isEvaluation}
                />
            )}
        </>
    )
}
